import React, { useState } from 'react';
import { Container, Typography, Button, Box, Collapse, Chip } from '@mui/material';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SectionHeading from './components/SectionHeading';
import SpotlightCard from './components/SpotlightCard';
import { Stagger } from './components/motion/Reveal';

export default function Careers() {
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const [expandedJob, setExpandedJob] = useState(null);

  const jobs = [
    {
      id: 'job1',
      title: 'Software Engineer',
      location: 'Remote · US',
      type: 'Full-Time',
      experience: '3+ Years',
      responsibilities: [
        'Develop and maintain ERP & CRM integrations for Salesforce & NetSuite.',
        'Work with React, Node.js, and cloud platforms to build scalable applications.',
        'Collaborate with cross-functional teams to enhance business workflows.',
        'Ensure high-quality code with best development practices.',
      ],
    },
    {
      id: 'job2',
      title: 'Senior Cloud Engineer',
      location: 'Remote · US',
      type: 'Full-Time',
      experience: '5+ Years',
      responsibilities: [
        'Design and implement scalable cloud solutions using AWS and Azure.',
        'Optimize cloud architecture for security, performance, and cost-efficiency.',
        'Integrate cloud infrastructure with applications alongside engineering teams.',
        'Lead the migration of legacy systems to modern cloud environments.',
      ],
    },
  ];

  const toggleExpand = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleApplyNow = (jobTitle) => {
    window.location.href = `mailto:careers@falconxcellence.com?subject=Application - ${jobTitle}&body=Dear Hiring Team,`;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 14, sm: 20 } }}>
      <SectionHeading
        eyebrow="Careers"
        title="Build the future,"
        highlight="with us"
        subtitle="We're looking for talented engineers to grow with us. Explore open roles below and click any listing to see the details."
        maxWidth={720}
      />

      <Stagger style={{ marginTop: 48 }} gap={0.12}>
        {jobs.map((job) => {
          const open = expandedJob === job.id;
          return (
            <Box key={job.id} sx={{ mb: 2.5 }}>
              <SpotlightCard tilt={false}>
                <Box
                  onClick={() => toggleExpand(job.id)}
                  sx={{ p: { xs: 3, sm: 3.5 }, cursor: 'pointer' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 700, fontSize: '1.25rem', fontFamily: '"Sora","Inter",sans-serif' }}
                    >
                      {job.title}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        border: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                        transform: open ? 'rotate(180deg)' : 'none',
                      }}
                    >
                      <KeyboardArrowDownRoundedIcon fontSize="small" />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {[
                      { icon: <LocationOnRoundedIcon sx={{ fontSize: 16 }} />, label: job.location },
                      { icon: <WorkOutlineRoundedIcon sx={{ fontSize: 16 }} />, label: job.type },
                      { icon: <TimelineRoundedIcon sx={{ fontSize: 16 }} />, label: job.experience },
                    ].map((chip) => (
                      <Chip
                        key={chip.label}
                        icon={chip.icon}
                        label={chip.label}
                        size="small"
                        sx={{
                          bgcolor: (t) =>
                            t.palette.mode === 'dark' ? 'hsla(210,90%,60%,0.08)' : 'hsla(210,90%,50%,0.06)',
                          borderRadius: 1.5,
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>

                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Roles & Responsibilities</Typography>
                      {job.responsibilities.map((r, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.25, mb: 1.25, alignItems: 'flex-start' }}>
                          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.2 }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                            {r}
                          </Typography>
                        </Box>
                      ))}
                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          px: 3,
                          color: '#fff !important',
                          border: 'none !important',
                          backgroundColor: 'hsl(230,90%,55%) !important',
                          backgroundImage:
                            'linear-gradient(100deg, hsl(210,98%,52%), hsl(265,90%,60%)) !important',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyNow(job.title);
                        }}
                      >
                        Apply Now
                      </Button>
                    </Box>
                  </Collapse>
                </Box>
              </SpotlightCard>
            </Box>
          );
        })}
      </Stagger>
    </Container>
  );
}
